import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import JewelCard from '../components/JewelCard';
import { useCart } from '../context/CartContext';
import './Home.css';

export default function Home() {
  const { addItem } = useCart();
  const [jewels, setJewels] = useState([]);
  const [prices, setPrices] = useState({ gold: null, silver: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const materials = ['gold', 'silver', 'gold_plated', 'fancy'];
        const jewelFetches = materials.map((m) =>
          fetch(`/api/jewels?material=${encodeURIComponent(m)}&limit=4`)
        );
        const [pricesRes, ...jewelResList] = await Promise.all([
          fetch('/api/live-prices'),
          ...jewelFetches,
        ]);

        const blocks = await Promise.all(
          jewelResList.map(async (res) => {
            if (!res.ok) return [];
            const data = await res.json();
            return Array.isArray(data.items) ? data.items : [];
          })
        );
        setJewels(blocks.flat());

        if (pricesRes.ok) {
          const priceData = await pricesRes.json();
          setPrices(priceData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPrice = (val) => {
    if (val == null || Number.isNaN(Number(val))) return '—';
    return `₹${Math.round(Number(val)).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <div className="page-center"><div className="spinner" /></div>;
  }

  if (error) {
    return <div className="page-center"><p className="error-text">Error: {error}</p></div>;
  }

  const goldJewels = jewels.filter(j => j.material === 'gold').slice(0, 4);
  const silverJewels = jewels.filter(j => j.material === 'silver').slice(0, 4);
  const goldPlatedJewels = jewels.filter(j => j.material === 'gold_plated').slice(0, 4);
  const fancyJewels = jewels.filter(j => j.material === 'fancy').slice(0, 4);

  return (
    <div className="home">
      {/* Hero Section */}
      <div className="home-hero">
        <div className="home-hero-inner">
          <p className="hero-eyebrow">Fine jewellery since tradition</p>
          <h1 className="home-hero-title">Vikky Gold Palace</h1>
          <div className="hero-title-divider" aria-hidden />
          <p className="hero-tagline">Explore gold, silver, gold plated, and bespoke fancy pieces</p>
        </div>

        {/* Live Price Ticker */}
        {(prices.gold || prices.silver) && (
          <div className="hero-price-ticker">
            <span className="ticker-dot" />
            <span className="ticker-label">Today's Rate (ex-GST)</span>
            {prices.gold && (
              <span className="ticker-item gold">
                Gold: {formatPrice(prices.gold.price_per_gram)}/g
              </span>
            )}
            {prices.silver && (
              <span className="ticker-item silver">
                Silver: {formatPrice(prices.silver.price_per_gram)}/g
              </span>
            )}
          </div>
        )}

        <div className="hero-sections">
          <Link to="/section/gold" className="hero-btn gold">Gold Collection</Link>
          <Link to="/section/silver" className="hero-btn silver">Silver Collection</Link>
          <Link to="/section/gold-plated" className="hero-btn gold-plated">Gold Plated</Link>
          <Link to="/section/fancy" className="hero-btn fancy">Fancy Items</Link>
        </div>
      </div>

      {jewels.length === 0 ? (
        <div className="page-center">
          <p>No jewels available yet. Check back soon!</p>
        </div>
      ) : (
        <>
          {goldJewels.length > 0 && (
            <section className="home-section">
              <div className="section-header">
                <h2>✨ Gold Collection</h2>
                <Link to="/section/gold" className="view-all">View All →</Link>
              </div>
              <div className="jewels-grid">
                {goldJewels.map(j => <JewelCard key={j.id} jewel={j} onAddToCart={addItem} />)}
              </div>
            </section>
          )}

          {silverJewels.length > 0 && (
            <section className="home-section">
              <div className="section-header">
                <h2>🪙 Silver Collection</h2>
                <Link to="/section/silver" className="view-all">View All →</Link>
              </div>
              <div className="jewels-grid">
                {silverJewels.map(j => <JewelCard key={j.id} jewel={j} onAddToCart={addItem} />)}
              </div>
            </section>
          )}

          {goldPlatedJewels.length > 0 && (
            <section className="home-section">
              <div className="section-header">
                <h2>✨ Gold Plated</h2>
                <Link to="/section/gold-plated" className="view-all">View All →</Link>
              </div>
              <div className="jewels-grid">
                {goldPlatedJewels.map(j => <JewelCard key={j.id} jewel={j} onAddToCart={addItem} />)}
              </div>
            </section>
          )}

          {fancyJewels.length > 0 && (
            <section className="home-section">
              <div className="section-header">
                <h2>💎 Fancy Items</h2>
                <Link to="/section/fancy" className="view-all">View All →</Link>
              </div>
              <div className="jewels-grid">
                {fancyJewels.map(j => <JewelCard key={j.id} jewel={j} onAddToCart={addItem} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
